import {SelectItem, SelectQuestionParagraph, TextItem} from '../models';
import {ErrorItem, SelectQuestionCrudParagraph} from './models';

const selectMarker = '[]';
const paragraphSeparator = '\n';

interface SelectPositionResult {
  start: number,
  end: number
}

function countSelects(value: string): number {
  let currentPos = 0;
  let result = 0;

  let position = value.indexOf(selectMarker, currentPos);
  while (position !== -1) {
    currentPos = position + selectMarker.length;
    result++;
    position = value.indexOf(selectMarker, currentPos);
  }
  return result;
}

function* findSelectPositions(value: string): Generator<SelectPositionResult> {
  let currentPos = 0;

  let position = value.indexOf(selectMarker, currentPos);
  while (position !== -1) {
    yield {start: position, end: position + selectMarker.length};
    currentPos = position + selectMarker.length;
    position = value.indexOf(selectMarker, currentPos);
  }
}

function deserializeParagraph(line: string, selectItems: (SelectItem | ErrorItem)[]):
  (TextItem | SelectItem | ErrorItem)[] {
  let result: (TextItem | SelectItem | ErrorItem)[] = [];
  let lastPosition = 0;

  function addTextItem(start: number, end: number) {
    if (start === end)
      return;
    const content = line.substring(start, end);
    let item: TextItem = {
      type: 'text',
      content: content
    };
    result.push(item);
  }

  function addSelectOrErrorItem() {
    const select = selectItems.pop();
    if (select === undefined)
      result.push({type: 'select-error', message: 'Missing select'});
    else
      result.push(select);
  }

  for (let selectPosition of findSelectPositions(line)) {
    addTextItem(lastPosition, selectPosition.start);
    addSelectOrErrorItem();
    lastPosition = selectPosition.end;
  }

  addTextItem(lastPosition, line.length);
  return result;
}

function splitLines(value: string): string[] {
  function notEmptyLine(line: string): boolean {
    line = line.trim();
    return (line !== '');
  }

  return value.split(paragraphSeparator).filter(notEmptyLine);
}

function deserializeParagraphs(
  value: string, items: (SelectItem | ErrorItem)[])
  : SelectQuestionCrudParagraph[] {
  // reversed, because we are going to pop select items.
  items.reverse();
  let result: SelectQuestionCrudParagraph[] = [];
  for (let line of splitLines(value)) {
    let paragraphItems = deserializeParagraph(line, items);
    result.push({items: paragraphItems});
  }
  return result;
}

function serializeParagraph(paragraph: SelectQuestionParagraph): string {
  let result: string[] = [];

  function addText(item: TextItem) {
    result.push(item.content);
  }

  function addSelect() {
    result.push(selectMarker);
  }

  for (let item of paragraph.items) {
    if (item.type === 'text')
      addText(item);
    if (item.type === 'select')
      addSelect();
  }
  return result.join('');
}

function serializeParagraphs(paragraphs: readonly SelectQuestionParagraph[]): string {
  return paragraphs.map((p) => serializeParagraph(p))
    .join(paragraphSeparator);
}

function selectItemsList(paragraphs: readonly SelectQuestionParagraph[]): SelectItem[] {
  const result: SelectItem[] = [];
  for (let paragraph of paragraphs)
    for (let item of paragraph.items)
      if (item.type === 'select')
        result.push(item);
  return result;
}

/******************************************************************************/

class SelectQuestionCrudHelper {
  readonly countSelects = countSelects;
  readonly deserializeParagraphs = deserializeParagraphs;

  readonly serializeParagraphs = serializeParagraphs;

  readonly selectItemsList = selectItemsList;
}

export const selectQuestionCrudHelper = new SelectQuestionCrudHelper();
